import React, { FC, MouseEvent } from "react";
import Spinner from "Shared/components/spinner";
import InputCheckbox, {
    InputCheckboxChanged,
} from "Shared/components/input-checkbox";
import InlineSvg from "Shared/components/inline-svg";
import Tooltip from "Shared/components/tooltip";
import { useTranslation } from "Shared/util/custom-hooks";
import {
    ColumnTypes,
    DataTableColumn,
    DataTableProps,
    DataTableRow,
} from "./data-table";
import styles from "./data-table.css";

type DataTableRowsProps = {
    columns: DataTableColumn[];
    getColumnTypeClass: (type: ColumnTypes | undefined) => void;
    getRowId?: (row: DataTableRow) => string; // Info (240912): Should aim to become mandatory at some point.
    getRowLink: DataTableProps["getRowLink"];
    handleRowClick: (event?: MouseEvent<HTMLTableRowElement>) => void;
    isMobileMode: boolean;
    isSelectableDisabled: DataTableProps["selectableRowDisabled"];
    loading: boolean | undefined;
    loadingBottomText: string | undefined;
    loadingTopText: string | undefined;
    onRowDeselected: (row: DataTableRow) => void;
    onRowSelected: (row: DataTableRow) => void;
    rowInformationMessage: ((row: DataTableRow) => void) | undefined;
    rows: DataTableRow[];
    selectedRows: DataTableRow[];
    useRowInformation: boolean;
    useSelectableRows: boolean;
    validRows: DataTableRow[];
};

const renderLoadingRow = (
    topText: string | undefined,
    bottomText: string | undefined,
) => (
    <tr className={styles.stretch}>
        <td colSpan={100}>
            {topText && <div className={styles.loadingText}>{topText}</div>}
            <Spinner customClass={styles.spinner} />
            {bottomText && (
                <div className={styles.loadingText}>{bottomText}</div>
            )}
        </td>
    </tr>
);

const renderEmptyRow = (nothingRegisteredText: string) => (
    <tr className={styles.stretch}>
        <td className={styles.empty} colSpan={100}>
            {nothingRegisteredText}
        </td>
    </tr>
);

const getCellText = (column: DataTableColumn, row: DataTableRow): string =>
    column.formatter
        ? column.formatter(
              column.key.split(".").reduce((acc, key) => acc?.[key], row),
              row,
          )
        : String(column.key.split(".").reduce((acc, key) => acc?.[key], row));

const getRowIds = (
    rows: DataTableRow[],
    getRowId: (row: DataTableRow) => string,
): string[] => rows.map((x) => getRowId(x));

const renderRow = (
    rowIndex: number,
    columns: DataTableRowsProps["columns"],
    getColumnTypeClass: DataTableRowsProps["getColumnTypeClass"],
    row: DataTableRow,
    getRowLink: DataTableRowsProps["getRowLink"],
    handleRowClick: DataTableRowsProps["handleRowClick"],
    isMobileMode: DataTableRowsProps["isMobileMode"],
    useSelectableRows: DataTableRowsProps["useSelectableRows"],
    selectedRows: DataTableRowsProps["selectedRows"],
    validRows: DataTableRowsProps["validRows"],
    onRowSelected: DataTableRowsProps["onRowSelected"],
    onRowDeselected: DataTableRowsProps["onRowDeselected"],
    useRowInformation: DataTableRowsProps["useRowInformation"],
    rowInformationMessage: DataTableRowsProps["rowInformationMessage"],
    isSelectableDisabled: DataTableRowsProps["isSelectableDisabled"],
    getRowId: DataTableRowsProps["getRowId"],
) => {
    const mobileSelectable = useSelectableRows && isMobileMode;
    const showSelectableColumn = useSelectableRows && !mobileSelectable;
    const rowLink = getRowLink && getRowLink(row);
    // Info (240912): Temp if/else-solution until all DataTables have implemented the 'getRowId' function.
    let isSelectableHidden: boolean;
    let isChecked: boolean;
    if (getRowId) {
        const rowId = getRowId(row);
        const validRowIds = getRowIds(validRows, getRowId);
        const selectedRowIds = getRowIds(selectedRows, getRowId);

        isSelectableHidden = useSelectableRows && !validRowIds.includes(rowId);
        isChecked = useSelectableRows && selectedRowIds.includes(rowId);
    } else {
        isSelectableHidden = useSelectableRows && !validRows.includes(row);
        isChecked = useSelectableRows && selectedRows.includes(row);
    }
    const isSelectable = useSelectableRows && !isSelectableHidden;

    const handleInputCheckboxChanged = ({
        isChecked,
    }: InputCheckboxChanged) => {
        if (isChecked) {
            onRowSelected(row);
        } else {
            onRowDeselected(row);
        }
    };

    const handleRowClicked = (event?: MouseEvent<HTMLTableRowElement>) => {
        const isMobileSelectable =
            mobileSelectable && !(isSelectableDisabled?.(row) ?? true);

        if (rowLink) {
            handleRowClick(event);
        } else if (isMobileSelectable) {
            const isTooltipClicked = !!(event?.target as HTMLElement)?.closest(
                ".tooltip-container, .tables-data-table_tooltipTriggerContainer",
            );

            if (!isTooltipClicked) {
                handleInputCheckboxChanged({ isChecked: !isChecked });
            }
        }
    };

    const rowInformationTooltip =
        useRowInformation &&
        rowInformationMessage &&
        rowInformationMessage(row);

    return (
        <tr
            className={`${rowLink ? styles.rowLink : ""} ${
                isSelectable ? styles.selectable : ""
            } ${isChecked ? styles.selected : ""} ${
                isSelectableHidden ? styles.disabled : ""
            }`}
            data-href={rowLink}
            key={rowIndex}
            onClick={handleRowClicked}
        >
            {showSelectableColumn && (
                <td className={styles.checkboxCell}>
                    {isSelectable && (
                        <InputCheckbox
                            checked={isChecked}
                            className={styles.customCheckbox}
                            disabled={isSelectableDisabled?.(row)}
                            onChange={handleInputCheckboxChanged}
                        />
                    )}
                </td>
            )}
            {columns.map((column, index) => (
                <td
                    data-title={column.title}
                    key={`${index + rowIndex}`}
                    className={`${getColumnTypeClass(column.type)} ${
                        column.hiddenMobile ? styles.hiddenMobile : ""
                    }`}
                >
                    {column.customCellRenderer ? (
                        column.customCellRenderer(row)
                    ) : column.oneLineEllipsis ? (
                        <span className={styles.textOverflowDynamicContainer}>
                            <span
                                className={styles.textOverflowDynamicEllipsis}
                            >
                                {getCellText(column, row)}
                            </span>
                        </span>
                    ) : (
                        getCellText(column, row)
                    )}
                </td>
            ))}
            {useRowInformation &&
                (rowInformationTooltip ? (
                    <td>
                        <Tooltip
                            placement={
                                isMobileMode ? "bottom-start" : "left-start"
                            }
                            tooltip={rowInformationTooltip}
                            triggerClassName={styles.tooltipTriggerContainer}
                            usePortal={false}
                        >
                            <InlineSvg
                                src="/information-warning.svg"
                                className={styles.informationSvgContainer}
                            />
                        </Tooltip>
                    </td>
                ) : (
                    <td className={styles.hiddenMobile} />
                ))}
        </tr>
    );
};

export const DataTableRows: FC<DataTableRowsProps> = ({
    columns,
    getColumnTypeClass,
    getRowId,
    getRowLink,
    handleRowClick,
    isMobileMode,
    isSelectableDisabled,
    loading,
    loadingBottomText,
    loadingTopText,
    onRowDeselected,
    onRowSelected,
    rowInformationMessage,
    rows,
    selectedRows,
    useRowInformation,
    useSelectableRows,
    validRows,
}) => {
    const t = useTranslation((x) => x.dataTable);

    if (loading) return renderLoadingRow(loadingTopText, loadingBottomText);
    if (rows.length <= 0) return renderEmptyRow(t.nothingRegistered);

    return (
        <>
            {rows.map((row, index) =>
                renderRow(
                    index,
                    columns,
                    getColumnTypeClass,
                    row,
                    getRowLink,
                    handleRowClick,
                    isMobileMode,
                    useSelectableRows,
                    selectedRows,
                    validRows,
                    onRowSelected,
                    onRowDeselected,
                    useRowInformation,
                    rowInformationMessage,
                    isSelectableDisabled,
                    getRowId,
                ),
            )}
        </>
    );
};
